import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import EmailGateModal from '../../../components/ui/EmailGateModal';
import { emailService } from '../../../utils/emailService';

const CalculationPanel = ({ formData, calculations, onViewProjections }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getRevenueBreakdown = () => {
    const totalSites = (formData?.siteInventory?.shortTermSites || 0) + (formData?.siteInventory?.longTermSites || 0);
    const occupancyRate = formData?.operational?.occupancyRate / 100;
    const daysOpen = formData?.operational?.daysOpenPerMonth || 30;

    // Calculate utility hookup multiplier based on selected utilities
    let utilityMultiplier = 1.0;
    const { power, water, sewer } = formData?.utilities || {};
    
    if (power && water && sewer) {
      // Full hookup - premium pricing (30% increase)
      utilityMultiplier = 1.30;
    } else if (power || water || sewer) {
      // Partial hookup - moderate pricing (15% increase)  
      utilityMultiplier = 1.15;
    }
    // Dry camping stays at base rate (1.0)

    // Calculate site features multiplier based on selected amenities
    let featuresMultiplier = 1.0;
    const premiumFeatures = ['waterfront', 'bigRigAccess', 'security', 'recreation'];
    const standardFeatures = ['petFriendly', 'pullThrough', 'wifi', 'laundry', 'restrooms', 'store'];
    
    const selectedFeatures = formData?.siteFeatures || [];
    const premiumCount = selectedFeatures?.filter(feature => premiumFeatures?.includes(feature))?.length;
    const standardCount = selectedFeatures?.filter(feature => standardFeatures?.includes(feature))?.length;
    
    // Premium features: 4% per feature, Standard features: 2% per feature
    featuresMultiplier = 1 + (premiumCount * 0.04) + (standardCount * 0.02);

    // Apply utility and feature multipliers to base pricing
    const adjustedNightlyRate = (formData?.pricing?.nightly || 45) * utilityMultiplier * featuresMultiplier;
    const adjustedWeeklyRate = (formData?.pricing?.weekly || 280) * utilityMultiplier * featuresMultiplier;  
    const adjustedMonthlyRate = (formData?.pricing?.monthly || 950) * utilityMultiplier * featuresMultiplier;

    // Calculate base revenue from different pricing tiers with adjusted rates
    const shortTermRevenue = (formData?.siteInventory?.shortTermSites || 0) * adjustedNightlyRate * occupancyRate * daysOpen * 0.7; // 70% short-term
    const weeklyRevenue = (formData?.siteInventory?.shortTermSites || 0) * (adjustedWeeklyRate / 7) * occupancyRate * daysOpen * 0.2; // 20% weekly
    const monthlyRevenue = (formData?.siteInventory?.longTermSites || 0) * (adjustedMonthlyRate / 30) * occupancyRate * daysOpen * 1.0; // 100% monthly

    const baseRevenue = shortTermRevenue + weeklyRevenue + monthlyRevenue;

    // Apply revenue multipliers (existing logic)
    let multiplierBoost = 1;
    formData?.revenueMultipliers?.forEach((multiplier) => {
      switch (multiplier) {
        case 'dynamicPricing':multiplierBoost += 0.20;break;
        case 'weekendPremium':multiplierBoost += 0.25;break;
        case 'lockSiteFees':multiplierBoost += 0.075;break;
        case 'eventPricing':multiplierBoost += 0.15;break;
        case 'earlyBooking':multiplierBoost += 0.125;break;
        case 'loyaltyProgram':multiplierBoost += 0.10;break;
        default:break;
      }
    });

    const grossRevenue = baseRevenue * multiplierBoost;

    // Calculate costs based on selected model
    let costPercentage = 0.50; // Default 50%
    switch (formData?.operational?.costModel) {
      case 'lean':costPercentage = 0.375;break; // 37.5% (middle of 30-45%)
      case 'standard':costPercentage = 0.525;break; // 52.5% (middle of 45-60%)
      case 'premium':costPercentage = 0.675;break; // 67.5% (middle of 60-75%)
      default:break;
    }

    const estimatedCosts = grossRevenue * costPercentage;
    const netIncome = grossRevenue - estimatedCosts;

    return {
      grossRevenue,
      estimatedCosts,
      netIncome,
      totalSites,
      occupancyRate: formData?.operational?.occupancyRate,
      multiplierBoost: (multiplierBoost - 1) * 100,
      // Add new calculation details for transparency
      utilityMultiplier: (utilityMultiplier - 1) * 100,
      featuresMultiplier: (featuresMultiplier - 1) * 100,
      adjustedNightlyRate,
      baseNightlyRate: formData?.pricing?.nightly || 45
    };
  };

  const handleViewProjections = () => {
    // First check if form is complete
    if (!calculations?.isFormComplete) {
      return; // Button should be disabled, but extra safety check
    }

    // Check if user already has access
    if (emailService?.hasAccess()) {
      // User has access, proceed to projections
      onViewProjections?.();
    } else {
      // Show email gate modal
      setShowEmailModal(true);
    }
  };

  const handleEmailSuccess = async (email, isExisting) => {
    // Save calculator session for analytics
    const breakdown = getRevenueBreakdown();
    const sessionData = {
      formData,
      calculations,
      breakdown,
      timestamp: new Date()?.toISOString(),
      source: 'rv_park_calculator'
    };

    // Save session in background (don't block UI)
    emailService?.saveCalculatorSession(email, sessionData, {
      monthlyIncome: breakdown?.grossRevenue / 12,
      calculatedRent: breakdown?.netIncome / 12,
      rentPercentage: Math.round(breakdown?.netIncome / breakdown?.grossRevenue * 100),
      disposableIncome: breakdown?.netIncome / 12,
      nonRentExpenses: breakdown?.estimatedCosts / 12
    })?.catch(console.error);

    // Close modal and proceed
    setShowEmailModal(false);

    // Brief delay for better UX, then navigate
    setTimeout(() => {
      onViewProjections?.();
    }, 100);
  };

  const breakdown = getRevenueBreakdown();
  const isFormComplete = calculations?.isFormComplete;

  return (
    <>
      <div className="bg-card rounded-lg border border-border shadow-lg sticky top-24">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
              <Icon name="Calculator" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground">Income Calculation</h3>
              <p className="text-sm text-muted-foreground">Real-time financial projections</p>
            </div>
          </div>

          {/* Monthly Net Income - Prominent Display */}
          <div className="text-center p-6 bg-primary/5 rounded-lg border border-primary/20">
            <div className="text-sm text-muted-foreground mb-1">Estimated Monthly Net Income</div>
            <div className="text-3xl font-bold text-primary mb-2">
              {formatCurrency(breakdown?.netIncome)}
            </div>
            <div className="text-xs text-muted-foreground">
              Based on {breakdown?.totalSites} sites at {breakdown?.occupancyRate}% occupancy
            </div>
            {(breakdown?.utilityMultiplier > 0 || breakdown?.featuresMultiplier > 0) && 
            <div className="text-xs text-success mt-1 font-medium">
                +{(breakdown?.utilityMultiplier + breakdown?.featuresMultiplier)?.toFixed(0)}% from utilities & features
              </div>
            }
          </div>
        </div>
        
        {/* Detailed Breakdown */}
        <div className="p-6">
          <h4 className="font-medium text-foreground mb-4">Revenue Breakdown</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Base Revenue</span>
              <span className="font-medium text-foreground">{formatCurrency(breakdown?.grossRevenue / (formData?.revenueMultipliers?.length > 0 ? breakdown?.multiplierBoost / 100 + 1 : 1))}</span>
            </div>

            {(breakdown?.utilityMultiplier > 0 || breakdown?.featuresMultiplier > 0) && 
            <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-success">Utilities & Features</span>
                <span className="font-medium text-success">
                  +{(breakdown?.utilityMultiplier + breakdown?.featuresMultiplier)?.toFixed(1)}%
                </span>
              </div>
            }
            
            {breakdown?.multiplierBoost > 0 &&
            <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-success">Revenue Multipliers</span>
                <span className="font-medium text-success">+{breakdown?.multiplierBoost?.toFixed(1)}%</span>
              </div>
            }

            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Gross Revenue</span>
              <span className="font-medium text-foreground">{formatCurrency(breakdown?.grossRevenue)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Estimated Costs</span>
              <span className="font-medium text-destructive">-{formatCurrency(breakdown?.estimatedCosts)}</span>
            </div>
            
            <div className="flex justify-between items-center py-3 bg-muted rounded-lg px-3">
              <span className="font-medium text-foreground">Net Income</span>
              <span className="font-bold text-primary">{formatCurrency(breakdown?.netIncome)}</span>
            </div>
          </div>

          {/* Annual Projection */}
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Annual Projection</span>
              <Icon name="TrendingUp" size={16} className="text-primary" />
            </div>
            <div className="text-lg font-bold text-primary">
              {formatCurrency(breakdown?.netIncome * 12)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Estimated yearly net income
            </div>
          </div>

          {/* Form Completion Note and Action Button */}
          <div className="mt-6 space-y-3">
            {/* Completion Status Note - Made smaller and less prominent */}
            {!isFormComplete &&
            <div className="text-center">
                <span className="text-[rgba(208,11,14,1)] text-[13px] font-bold">Complete RV Park Income Data First</span>
              </div>
            }

            {/* Action Button - Made bigger and more prominent */}
            <Button
              onClick={handleViewProjections}
              className="w-full py-4 text-lg font-semibold shadow-lg border-2 border-primary/20"
              iconName="BarChart3"
              iconPosition="left"
              disabled={!isFormComplete}
              variant={!isFormComplete ? "outline" : "default"}
              size="lg">

              View 5-Year Projections
            </Button>
            
            {/* Access Status Indicator */}
            <div className="text-center">
              {!isFormComplete ?
              <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
                  <Icon name="Lock" size={14} />
                  <span>Complete all required sections to unlock</span>
                </div> :
              emailService?.hasAccess() ?
              <div className="flex items-center justify-center space-x-2 text-xs text-success">
                  <Icon name="CheckCircle" size={14} />
                  <span>Access Granted</span>
                </div> :

              <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
                  <Icon name="Lock" size={14} />
                  <span>Email required for access</span>
                </div>
              }
            </div>
          </div>
        </div>
        
        {/* Key Metrics */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground">Revenue per Site</div>
              <div className="font-bold text-foreground">
                {breakdown?.totalSites > 0 ? formatCurrency(breakdown?.grossRevenue / breakdown?.totalSites) : '$0'}
              </div>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground">Profit Margin</div>
              <div className="font-bold text-foreground">
                {breakdown?.grossRevenue > 0 ? Math.round(breakdown?.netIncome / breakdown?.grossRevenue * 100) : 0}%
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Email Gate Modal */}
      <EmailGateModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
        title="Access Your RV Park Financial Projections" />

    </>);

};

export default CalculationPanel;